/**
 * Phase 8 T-20 §24.A — Step 2: Availability
 *
 * Three sub-sections:
 *   1. Available hours — Start / End hh:mm am/pm (30-min increments from 12:00 am)
 *   2. Buffer time — Before meetings / After meetings (None / 5 / 10 / 15 / 30 min)
 *   3. Advance booking — Max days in advance (1 week / 2 weeks / 1 month / 3 months)
 */

import type { BookingConfig } from './BookingFlow';

// ── Time options ─────────────────────────────────────────────────────────────

/** Build all 30-min slots in 12-hour format, e.g. "12:00 am", "12:30 am", "1:00 am" … "11:30 pm" */
function buildTimeSlots(): { value: string; label: string }[] {
  const slots: { value: string; label: string }[] = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 30]) {
      const period = h < 12 ? 'am' : 'pm';
      const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const displayMin = m === 0 ? '00' : '30';
      const label = `${displayHour}:${displayMin} ${period}`;
      // value = HH:MM (24h for storage; display is 12h)
      const valueHour = String(h).padStart(2, '0');
      const valueMin = String(m).padStart(2, '0');
      slots.push({ value: `${valueHour}:${valueMin}`, label });
    }
  }
  return slots;
}

const TIME_SLOTS = buildTimeSlots();

const BUFFER_OPTIONS = [
  { value: '0', label: 'None' },
  { value: '5', label: '5 min' },
  { value: '10', label: '10 min' },
  { value: '15', label: '15 min' },
  { value: '30', label: '30 min' },
];

const ADVANCE_OPTIONS = [
  { value: '7', label: '1 week' },
  { value: '14', label: '2 weeks' },
  { value: '30', label: '1 month' },
  { value: '90', label: '3 months' },
];

// ── Shared select ────────────────────────────────────────────────────────────

const selectCls =
  'rounded-[8px] border px-2 py-1.5 text-[13px] vscode-border vscode-bg-secondary vscode-text-primary focus:outline-none focus:ring-1 focus:ring-[#10B981]';

// ── Row layout ───────────────────────────────────────────────────────────────

function SettingRow({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p className="text-[13px] font-medium vscode-text-primary">{label}</p>
        {hint && <p className="mt-0.5 text-[11px] vscode-text-tertiary">{hint}</p>}
      </div>
      <div className="flex shrink-0 items-center gap-2">{children}</div>
    </div>
  );
}

// ── AvailabilityStep ─────────────────────────────────────────────────────────

interface AvailabilityStepProps {
  config: BookingConfig;
  onChange: (patch: Partial<BookingConfig>) => void;
}

export function AvailabilityStep({ config, onChange }: AvailabilityStepProps) {
  return (
    <div className="flex flex-col gap-6">
      {/* Available hours */}
      <section>
        <h3 className="mb-3 text-[13px] font-semibold vscode-text-primary">Available hours</h3>
        <div className="flex flex-col gap-3 rounded-[10px] border px-4 py-3 vscode-border vscode-bg-secondary">
          <SettingRow label="Start">
            <select
              value={config.availStart}
              onChange={(e) => onChange({ availStart: e.target.value })}
              className={selectCls}
              aria-label="Available hours start time"
            >
              {TIME_SLOTS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </SettingRow>
          <div className="border-t vscode-border" />
          <SettingRow label="End">
            <select
              value={config.availEnd}
              onChange={(e) => onChange({ availEnd: e.target.value })}
              className={selectCls}
              aria-label="Available hours end time"
            >
              {TIME_SLOTS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </SettingRow>
        </div>
      </section>

      {/* Buffer time */}
      <section>
        <h3 className="mb-3 text-[13px] font-semibold vscode-text-primary">Buffer time</h3>
        <div className="flex flex-col gap-3 rounded-[10px] border px-4 py-3 vscode-border vscode-bg-secondary">
          <SettingRow
            label="Before meetings"
            hint="Extra time blocked before each interview"
          >
            <select
              value={String(config.bufferBefore)}
              onChange={(e) => onChange({ bufferBefore: Number(e.target.value) })}
              className={selectCls}
              aria-label="Buffer time before meetings"
            >
              {BUFFER_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </SettingRow>
          <div className="border-t vscode-border" />
          <SettingRow
            label="After meetings"
            hint="Extra time blocked after each interview"
          >
            <select
              value={String(config.bufferAfter)}
              onChange={(e) => onChange({ bufferAfter: Number(e.target.value) })}
              className={selectCls}
              aria-label="Buffer time after meetings"
            >
              {BUFFER_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </SettingRow>
        </div>
      </section>

      {/* Advance booking */}
      <section>
        <h3 className="mb-3 text-[13px] font-semibold vscode-text-primary">Advance booking</h3>
        <div className="rounded-[10px] border px-4 py-3 vscode-border vscode-bg-secondary">
          <SettingRow
            label="Max days in advance"
            hint="How far ahead candidates can schedule"
          >
            <select
              value={String(config.advanceDays)}
              onChange={(e) => onChange({ advanceDays: Number(e.target.value) })}
              className={selectCls}
              aria-label="Maximum days in advance for booking"
            >
              {ADVANCE_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </SettingRow>
        </div>
      </section>
    </div>
  );
}
